import React, { useCallback } from "react";
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { Users } from "lucide-react";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData, Country, EntitySnapshot } from "../../types";
import { PartnerAutocomplete } from "../shipments/PartnerAutoComplete";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import { useTranslation } from "react-i18next";

interface CarrierClientSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  setValue: UseFormSetValue<CreateShipmentData>;
  watch: UseFormWatch<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  countries: Country[];
  countryOptions: Array<{ value: string; label: string }>;
  apiKey: string;
  clientSyncMode: "none" | "pickup" | "delivery";
  /*
   * DEAD PROPS — kept commented for reference. Drove the legacy modal-based
   * Add/Edit-client flow. Replaced by inline fields rendered below the
   * PartnerAutocomplete in this component.
   *
   *   clientSnapshot?: EntitySnapshot;
   *   onClientSnapshotSelect: (snapshot: EntitySnapshot) => void;
   *   onClientAdd: () => void;
   *   onClientEdit: () => void;
   *   onClientClear: () => void;
   */
}

export const CarrierClientSection: React.FC<CarrierClientSectionProps> = ({
  register,
  setValue,
  watch,
  errors,
  readonly,
  getErrorMessage,
  countries,
  countryOptions,
  apiKey,
}) => {
  const watchClientCompanyName = watch("client.companyName");
  const watchClientStreet = watch("client.address.street");

  const handleClientSnapshotSelect = useCallback(
    (snapshot: EntitySnapshot) => {
      const country = snapshot?.address?.country;
      // Capture the Partner link for invoicing. The autocomplete fires onChange
      // (which clears partnerId) just before this, so setting it here wins.
      setValue("client.partnerId", snapshot._id ?? null);
      setValue("client.companyName", snapshot.companyName);
      setValue("client.registerationCode", snapshot.registerationCode || "");
      setValue("client.phoneNumber", snapshot.phoneNumber || "");
      setValue("client.emailAddress", snapshot.emailAddress || "");
      setValue("client.address.street", snapshot.address.street);
      setValue("client.address.city", snapshot.address.city);
      setValue("client.address.state", snapshot.address.state);
      setValue("client.address.postalCode", snapshot.address.postalCode || "");
      setValue(
        "client.address.country",
        typeof country === "object" && country !== null ? country._id : country,
      );
    },
    [setValue],
  );

  const handleClientClear = useCallback(() => {
    setValue("client.partnerId", null);
    setValue("client.companyName", "");
    setValue("client.registerationCode", "");
    setValue("client.phoneNumber", "");
    setValue("client.emailAddress", "");
    setValue("client.address.street", "");
    setValue("client.address.city", "");
    setValue("client.address.state", "");
    setValue("client.address.postalCode", "");
    setValue("client.address.country", "");
  }, [setValue]);

  const handleClientAddressSelect = useCallback(
    (addressData: any) => {
      setValue("client.address.street", addressData.street);
      setValue("client.address.city", addressData.city);
      setValue("client.address.state", addressData.state);
      setValue("client.address.postalCode", addressData.postalCode);
      setValue("client.address.country", addressData.country);
    },
    [setValue],
  );

  const { t } = useTranslation("shipments");

  return (
    <SectionCard icon={Users} title={t("client.title")} className="basis-1/2">
      <div className="space-y-1">
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.company")}
          error={getErrorMessage(errors.client?.companyName)}
        >
          <PartnerAutocomplete
            value={watchClientCompanyName || ""}
            onChange={(value) => {
              // Typing a name by hand means this is no longer a directory
              // selection — drop the Partner link (re-set on snapshot select).
              setValue("client.partnerId", null);
              setValue("client.companyName", value);
            }}
            onSnapshotSelect={handleClientSnapshotSelect}
            onClear={handleClientClear}
            placeholder={t("client.companyPlaceholder")}
            disabled={readonly}
            error={!!errors.client?.companyName}
            addresses={false}
            type="client"
            size="sm"
          />
        </FormField>

        {/* Explicit hidden field so the Partner link is always part of the form
            payload (driven by setValue on select/clear/type). Guarantees
            client.partnerId is submitted regardless of RHF field registration. */}
        <input type="hidden" {...register("client.partnerId")} />

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.regCode")}
          error={getErrorMessage(errors.client?.registerationCode)}
        >
          <Input
            {...register("client.registerationCode")}
            size="sm"
            error={!!errors.client?.registerationCode}
            placeholder={t("placeholders.regCode")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.phone")}
          error={getErrorMessage(errors.client?.phoneNumber)}
        >
          <Input
            {...register("client.phoneNumber")}
            size="sm"
            error={!!errors.client?.phoneNumber}
            placeholder={t("placeholders.phone")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.email")}
          error={getErrorMessage(errors.client?.emailAddress)}
        >
          <Input
            {...register("client.emailAddress")}
            type="email"
            size="sm"
            error={!!errors.client?.emailAddress}
            placeholder={t("placeholders.email")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.address")}
          error={getErrorMessage(errors.client?.address?.street)}
        >
          <AddressAutocompleteField
            apiKey={apiKey}
            value={watchClientStreet || ""}
            onChange={(value) => setValue("client.address.street", value)}
            onAddressSelect={handleClientAddressSelect}
            placeholder={t("client.addressPlaceholder")}
            disabled={readonly}
            error={!!errors.client?.address?.street}
            countries={countries}
            size="sm"
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.city")}
          error={getErrorMessage(errors.client?.address?.city)}
        >
          <Input
            {...register("client.address.city")}
            size="sm"
            error={!!errors.client?.address?.city}
            placeholder={t("placeholders.city")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.state")}
          error={getErrorMessage(errors.client?.address?.state)}
        >
          <Input
            {...register("client.address.state")}
            size="sm"
            error={!!errors.client?.address?.state}
            placeholder={t("placeholders.state")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.postalCode")}
          error={getErrorMessage(errors.client?.address?.postalCode)}
        >
          <Input
            {...register("client.address.postalCode")}
            size="sm"
            error={!!errors.client?.address?.postalCode}
            placeholder={t("placeholders.postalCode")}
            readOnly={readonly}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.country")}
          error={getErrorMessage(errors.client?.address?.country)}
        >
          <Select
            {...register("client.address.country")}
            size="sm"
            error={!!errors.client?.address?.country}
            options={countryOptions}
            disabled={readonly}
            placeholder={t("placeholders.country")}
          />
        </FormField>
      </div>
    </SectionCard>
  );
};
